import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../context/AuthContext'
import { apiClient } from '../../lib/apiClient'
import { taskService, AssignedTask, minutesToHours, toMinutesNumber } from '../../services/taskService'
import { Badge } from '../ui/Badge'
import { ClipboardList, AlertTriangle } from '../icons/Icons'
import s from './shared.module.css'

type StatusFilter = 'active' | 'all' | 'done'
type SortKey = 'priority' | 'due_date' | 'title'

const PRIORITY_COLORS: Record<string, string> = {
  low: '#6c757d',
  medium: '#0d6efd',
  high: '#fd7e14',
  critical: '#dc3545',
}

const PRIORITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
}

const STATUS_LABELS: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  done: 'Completed',
  blocked: 'Blocked',
}

const STATUS_COLORS: Record<string, string> = {
  todo: '#6c757d',
  in_progress: '#0d6efd',
  done: '#198754',
  blocked: '#dc3545',
}

interface Resource {
  id: string
  title: string
  email: string
  owner_user_id?: string | null
}

export default function MyTasks() {
  const { organization, user, orgMember } = useAuth()
  const orgId = organization!.id

  const [resource, setResource] = useState<Resource | null>(null)
  const [tasks, setTasks] = useState<AssignedTask[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active')
  const [sortKey, setSortKey] = useState<SortKey>('priority')

  useEffect(() => {
    loadResourceAndTasks()
  }, [orgId])

  const loadResourceAndTasks = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await apiClient.get<Resource>(
        `/orgs/${orgId}/resources/for-current-user`,
      )
      if (!res) {
        setError('No resource profile is linked to your account. Please ask a manager to link your resource profile.')
        setLoading(false)
        return
      }
      setResource(res)
      const data = await taskService.getAllAssignedTasks(orgId, res.id)
      setTasks(data)
    } catch (err: any) {
      setError(err?.message || 'Failed to load your tasks')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (taskId: string, newStatus: AssignedTask['status']) => {
    try {
      await taskService.updateTask(taskId, { status: newStatus })
      setTasks(prev =>
        prev.map(t => (t.id === taskId ? { ...t, status: newStatus } : t)),
      )
    } catch (err) {
      console.error('Failed to update task status:', err)
    }
  }

  const filteredTasks = useMemo(() => {
    let result = [...tasks]

    if (statusFilter === 'active') {
      result = result.filter(t => t.status !== 'done')
    } else if (statusFilter === 'done') {
      result = result.filter(t => t.status === 'done')
    }

    result.sort((a, b) => {
      if (sortKey === 'priority') {
        const diff = (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99)
        if (diff !== 0) return diff
        const aDate = a.due_date || '9999-12-31'
        const bDate = b.due_date || '9999-12-31'
        return aDate.localeCompare(bDate)
      }
      if (sortKey === 'due_date') {
        const aDate = a.due_date || '9999-12-31'
        const bDate = b.due_date || '9999-12-31'
        return aDate.localeCompare(bDate)
      }
      return a.title.localeCompare(b.title)
    })

    return result
  }, [tasks, statusFilter, sortKey])

  const stats = useMemo(() => {
    const total = tasks.length
    const active = tasks.filter(t => t.status !== 'done').length
    const done = tasks.filter(t => t.status === 'done').length
    const critical = tasks.filter(t => t.priority === 'critical' && t.status !== 'done').length
    const totalBudgetedMinutes = tasks.reduce((sum, t) => sum + toMinutesNumber(t.estimated_minutes), 0)
    const overBudget = tasks.filter(t => {
      const budgetMinutes = toMinutesNumber(t.estimated_minutes)
      return budgetMinutes > 0 && toMinutesNumber(t.logged_minutes) > budgetMinutes
    }).length
    return {
      total,
      active,
      done,
      critical,
      totalBudgetedHours: minutesToHours(totalBudgetedMinutes),
      overBudget,
    }
  }, [tasks])

  const isOverBudget = (task: AssignedTask): boolean => {
    const budgetMinutes = toMinutesNumber(task.estimated_minutes)
    return budgetMinutes > 0 && toMinutesNumber(task.logged_minutes) > budgetMinutes
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>My Tasks</h2>
          <p className={s.pageSubtitle}>
            {resource ? `Tasks assigned to ${resource.title}` : 'Loading your task assignments...'}
          </p>
        </div>
      </div>

      {error && (
        <div className={s.formError} style={{ marginBottom: 24 }}>{error}</div>
      )}

      {/* Budget reminder banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '14px 18px',
          marginBottom: 24,
          borderRadius: 10,
          backgroundColor: 'rgba(253, 126, 20, 0.08)',
          border: '1px solid rgba(253, 126, 20, 0.3)',
        }}
      >
        <AlertTriangle size={20} style={{ color: '#fd7e14', flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#b35900', marginBottom: 2 }}>
            Budget Reminder
          </div>
          <div style={{ fontSize: 13, color: '#8a4a00', lineHeight: 1.5 }}>
            Do not log more hours than the budgeted amount on a task without prior approval from your manager.
            exceeding the budget without approval causes revenue leakage and may affect project profitability.
          </div>
        </div>
      </div>

      <div className={s.statsGrid}>
        <div className={s.statCard}>
          <ClipboardList size={28} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)' }}>{stats.total}</div>
            <div style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>Total Tasks</div>
          </div>
        </div>
        <div className={s.statCard}>
          <div
            style={{
              width: 28, height: 28, borderRadius: '50%',
              backgroundColor: 'rgba(13, 110, 253, 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, color: '#0d6efd', flexShrink: 0,
            }}
          >
            {stats.active}
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)' }}>{stats.active}</div>
            <div style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>Active</div>
          </div>
        </div>
        <div className={s.statCard}>
          <div
            style={{
              width: 28, height: 28, borderRadius: '50%',
              backgroundColor: 'rgba(220, 53, 69, 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, color: '#dc3545', flexShrink: 0,
            }}
          >
            {stats.critical}
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)' }}>{stats.critical}</div>
            <div style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>Critical Priority</div>
          </div>
        </div>
        <div className={s.statCard}>
          <div
            style={{
              width: 28, height: 28, borderRadius: '50%',
              backgroundColor: 'rgba(25, 135, 84, 0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, color: '#198754', flexShrink: 0,
            }}
          >
            {stats.totalBudgetedHours}h
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)' }}>{stats.totalBudgetedHours}h</div>
            <div style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>Total Budgeted</div>
          </div>
        </div>
        {stats.overBudget > 0 && (
          <div
            className={s.statCard}
            style={{ borderColor: 'rgba(220, 53, 69, 0.3)', backgroundColor: 'rgba(254, 226, 226, 0.5)' }}
          >
            <div
              style={{
                width: 28, height: 28, borderRadius: '50%',
                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color: '#dc3545', flexShrink: 0,
              }}
            >
              {stats.overBudget}
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#dc3545' }}>{stats.overBudget}</div>
              <div style={{ fontSize: 13, color: '#dc3545' }}>Over Budget</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-neutral-700)' }}>Status:</label>
        <select
          className={s.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="active">Active (hide completed)</option>
          <option value="all">All tasks</option>
          <option value="done">Completed only</option>
        </select>

        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-neutral-700)', marginLeft: 8 }}>Sort by:</label>
        <select
          className={s.filterSelect}
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
        >
          <option value="priority">Priority</option>
          <option value="due_date">Due Date</option>
          <option value="title">Task Name</option>
        </select>
      </div>

      <div className={s.card}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-600)' }}>
            Loading your tasks...
          </div>
        ) : filteredTasks.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-600)' }}>
            {error ? '' : 'No tasks assigned to you yet.'}
          </div>
        ) : (
          <div className={s.tableScroll}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Project</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Budget / Logged</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => {
                  const over = isOverBudget(task)
                  const budgetHours = task.estimated_minutes ? minutesToHours(task.estimated_minutes) : null
                  const loggedHours = task.logged_minutes ? minutesToHours(task.logged_minutes) : 0
                  const draftHours = task.draft_minutes ? minutesToHours(task.draft_minutes) : 0

                  return (
                    <tr key={task.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{task.title}</div>
                        {task.description && (
                          <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4, maxWidth: 360 }}>
                            {task.description.length > 100
                              ? task.description.substring(0, 100) + '...'
                              : task.description}
                          </div>
                        )}
                        {task.parent_task_title && (
                          <div style={{ fontSize: 11, color: 'var(--color-neutral-500)', marginTop: 4 }}>
                            Sub-task of: {task.parent_task_title}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ fontSize: 13, color: 'var(--color-neutral-700)' }}>
                          {task.project_title || '—'}
                        </span>
                      </td>
                      <td>
                        <Badge
                          style={{
                            backgroundColor: PRIORITY_COLORS[task.priority] || '#6c757d',
                            color: 'white',
                          }}
                        >
                          {task.priority}
                        </Badge>
                      </td>
                      <td>
                        <select
                          value={task.status}
                          onChange={(e) => handleStatusChange(task.id, e.target.value as AssignedTask['status'])}
                          style={{
                            padding: '4px 10px',
                            border: `1px solid ${STATUS_COLORS[task.status] || '#dee2e6'}`,
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            color: STATUS_COLORS[task.status] || '#495057',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                          }}
                        >
                          {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {budgetHours !== null ? (
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: over ? '#dc3545' : 'var(--color-neutral-900)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            {budgetHours}h / {loggedHours}h
                            {over && (
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.4,
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  backgroundColor: 'rgba(220, 53, 69, 0.12)',
                                  color: '#dc3545',
                                }}
                              >
                                Over Budget
                              </span>
                            )}
                            {draftHours > 0 && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: 'var(--color-neutral-500)',
                                }}
                              >
                                (+{draftHours}h draft)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-neutral-500)' }}>
                            {loggedHours > 0 ? `${loggedHours}h logged` : '—'}
                          </span>
                        )}
                      </td>
                      <td>
                        {task.due_date ? (
                          <span
                            style={{
                              color: isOverdue(task.due_date, task.status) ? '#dc3545' : 'var(--color-neutral-700)',
                              fontWeight: isOverdue(task.due_date, task.status) ? 600 : 400,
                            }}
                          >
                            {formatDate(task.due_date)}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function isOverdue(dueDate: string, status: string): boolean {
  if (status === 'done') return false
  const today = new Date().toISOString().substring(0, 10)
  return dueDate < today
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
